import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { addClientApplicationFiles, deleteClientApplicationFile, signClientApplicationUpload } from '../../api/Rover/clientApplication';

import { notifyUser } from '../../store/Toast';
import type { FilesToUpload, SignDeveloperAssetPayload, SignedApplicationAssetResponse } from '../../types/Shared';
import { useFileUpload } from '../useFileUpload';

interface UseClientApplicationFileProps {
  onSuccess?: () => void;
}
export const useClientApplicationFile = (props: UseClientApplicationFileProps) => {
  const { onSuccess } = props;
  const { upload } = useFileUpload({
    signAsset: signClientApplicationUpload,
  });
  const dispatch = useDispatch();

  const uploadFile = useMutation(
    (payload: { signAssetPayload: SignDeveloperAssetPayload[]; files: File[]; applicationId: number }) => upload(payload),
    {
      onSuccess: (response: any, variables) => {
        patchApplication.mutate({
          ...response,
          applicationId: variables.applicationId,
        });
      },
      onError: (_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to upload document',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const patchApplication = useMutation(
    ({ sign, files, applicationId }: { sign: SignedApplicationAssetResponse; files: File[]; response: any; applicationId: number }) => {
      const filesToUpload: FilesToUpload[] = [
        {
          key: sign.key,
          name: files[0].name,
          path: sign.path,
          type: files[0].type,
          description: sign.field || '',
          field: sign.field || '',
        },
      ];
      return addClientApplicationFiles({
        applicationId,
        files: filesToUpload,
      });
    },
    {
      onSuccess: (_response: any) => {
        if (onSuccess) {
          onSuccess();
        }
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Successfully Updated Client Application',
            toastType: 'success',
          })
        );
      },
      onError: (_error: any) => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Failed to upload document',
            toastType: 'failed',
          })
        );
      },
    }
  );

  const deleteFileMutation = useMutation((payload: { key: string; applicationId: number }) => deleteClientApplicationFile(payload), {
    onSuccess: () => {
      if (onSuccess) {
        onSuccess();
      }
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Successfully deleted file',
          toastType: 'success',
        })
      );
    },
    onError: (_error: any) => {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: 'Failed to delete file',
          toastType: 'failed',
        })
      );
    },
  });

  const uploadFiles = (applicationId: number, files: File[], field: string) => {
    uploadFile.mutate({
      signAssetPayload: [
        {
          fileName: files[0].name,
          fileType: files[0].type,
          fileSize: files[0].size,
          field,
        },
      ],
      files,
      applicationId,
    });
  };

  const deleteFile = (key: string, applicationId: number) => {
    deleteFileMutation.mutate({ key, applicationId });
  };

  return {
    uploadFiles,
    deleteFile,
    isLoading: uploadFile.isLoading || patchApplication.isLoading,
    deletingFile: deleteFileMutation.isLoading,
  };
};
